import { useCallback } from 'react';
import { useToast } from '@/lib/ui';
import { isAppError } from '@/lib/api/errors';

/**
 * Maps a thrown AppError to the right toast treatment (plan §4.1 / §5.1). Validation errors
 * are handled inline by forms, so this skips them by default. Always surfaces request_id.
 */
type ApiErrorOpts = { onValidation?: (fields: Record<string, string>) => void };

/**
 * The first control on screen the form has just marked invalid.
 *
 * Scoped to the open dialog when there is one: a record page keeps its own
 * fields mounted behind a modal, and the field that failed is the one inside it.
 * Disabled controls are skipped because they cannot take focus.
 */
function firstInvalidControl(): HTMLElement | null {
  const scope =
    document.querySelector('[role="dialog"][data-state="open"]') ??
    document.querySelector('[role="alertdialog"][data-state="open"]') ??
    document;
  return scope.querySelector<HTMLElement>('[aria-invalid="true"]:not([disabled])');
}

/**
 * Run after React has committed and painted the state this handler just set.
 * A single frame is enough — the setState happens in a promise callback, which
 * React 18 flushes in a microtask, well before the next animation frame.
 */
function afterRender(fn: () => void): void {
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(fn);
  else setTimeout(fn, 0);
}

export function useApiError() {
  const toast = useToast();
  // Rest params keep this assignable to react-query's onError(error, variables, context)
  // for any mutation variable type, while still accepting our optional opts object.
  return useCallback(
    (error: unknown, ...rest: unknown[]) => {
      const opts = rest[0] as ApiErrorOpts | undefined;
      if (!isAppError(error)) {
        toast.error('Something went wrong.', {
          description: error instanceof Error ? error.message : undefined,
        });
        return;
      }
      switch (error.code) {
        case 'validation_error': {
          if (!opts?.onValidation) {
            toast.error('Please fix the highlighted fields.', { requestId: error.requestId });
            break;
          }
          opts.onValidation(error.fieldErrors());
          // Passing `onValidation` used to give up the toast unconditionally, on
          // the form's word that it would render the errors itself. It cannot
          // always keep that promise: a 422 with no details at all, or one
          // naming a field this screen does not render, leaves the operator with
          // a refused save and no feedback anywhere (F-23). So the fallback is
          // decided by what actually reached the screen, not by intent.
          //
          // The same measurement answers the other half: a rejected save left
          // focus on <body>, so a keyboard user was returned to the top of the
          // document with the invalid field somewhere below (F-20). Fixed here,
          // in the shared handler, so every form that reports validation inline
          // inherits it rather than each re-implementing it.
          afterRender(() => {
            const invalid = firstInvalidControl();
            if (!invalid) {
              toast.error(error.message || 'That change was refused.', {
                description: 'The server did not accept it, and did not say which field.',
                requestId: error.requestId,
              });
              return;
            }
            invalid.focus();
            // A record header is sticky; focus alone can scroll the field under
            // it. Centring is the only position that is safe on every screen.
            // (jsdom has no layout and no `scrollIntoView` — the focus move is
            // the part that matters, and it must not be lost to a TypeError.)
            if (typeof invalid.scrollIntoView === 'function') {
              invalid.scrollIntoView({ block: 'center' });
            }
          });
          break;
        }
        case 'forbidden':
          toast.warning("You don't have permission to do that.");
          break;
        case 'conflict':
          toast.error(error.message || 'That identifier is already in use.', {
            requestId: error.requestId,
          });
          break;
        case 'rate_limited':
          toast.warning('Too many requests. Please wait a moment and try again.');
          break;
        case 'not_found':
          toast.error('That item no longer exists.');
          break;
        default:
          toast.error(error.message || 'Something went wrong.', { requestId: error.requestId });
      }
    },
    [toast],
  );
}
